import moment from 'moment/moment';
import React, { useState } from 'react';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Full month-view calendar popup for the "Choose Date & Time" step, opened by
// tapping the month label. Lets a student jump to any future date instead of
// only the next-14-days quick-pick strip, and (once blockedDates is wired up
// to a real source) can grey out instructor-unavailable days the same way
// the instructor panel's own calendar does.
const BookingCalendar = ({ selectedDateStr, onSelectDay, onClose, blockedDates = [] }) => {
    const [month, setMonth] = useState(moment(selectedDateStr, 'YYYY-MM-DD').startOf('month'));

    const today = moment().startOf('day');
    const startOfMonth = month.clone().startOf('month');
    const endOfMonth = month.clone().endOf('month');
    const startOfGrid = startOfMonth.clone().startOf('week');
    const endOfGrid = endOfMonth.clone().endOf('week');

    const days = [];
    const cursor = startOfGrid.clone();
    while (cursor.isSameOrBefore(endOfGrid, 'day')) {
        days.push(cursor.clone());
        cursor.add(1, 'day');
    }

    const isBlocked = (day) =>
        blockedDates.some(
            (range) =>
                day.isSameOrAfter(moment(range.startDate), 'day') &&
                day.isSameOrBefore(moment(range.endDate), 'day')
        );

    const canGoPrevMonth = month.clone().subtract(1, 'month').endOf('month').isSameOrAfter(today, 'day');

    return (
        <div className="booking-calendar-overlay" onClick={onClose}>
            <div className="booking-calendar-panel" onClick={(e) => e.stopPropagation()}>
                <div className="booking-calendar-header">
                    <h5>{month.format('MMMM YYYY')}</h5>
                    <div className="booking-calendar-nav">
                        <button
                            type="button"
                            disabled={!canGoPrevMonth}
                            onClick={() => setMonth((m) => m.clone().subtract(1, 'month'))}
                            aria-label="Previous month"
                        >
                            <i className="bi bi-chevron-left"></i>
                        </button>
                        <button
                            type="button"
                            onClick={() => setMonth((m) => m.clone().add(1, 'month'))}
                            aria-label="Next month"
                        >
                            <i className="bi bi-chevron-right"></i>
                        </button>
                        <button type="button" className="booking-calendar-close" onClick={onClose} aria-label="Close">
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>
                </div>

                <div className="booking-calendar-grid booking-calendar-grid-head">
                    {WEEKDAYS.map((day) => (
                        <div key={day} className="booking-calendar-cell booking-calendar-cell-head">
                            {day}
                        </div>
                    ))}
                </div>

                <div className="booking-calendar-grid">
                    {days.map((day) => {
                        const inMonth = day.isSame(month, 'month');
                        const isPast = day.isBefore(today, 'day');
                        const blocked = isBlocked(day);
                        const isDisabled = isPast || blocked;
                        const isSelected = day.format('YYYY-MM-DD') === selectedDateStr;
                        const isToday = day.isSame(today, 'day');

                        return (
                            <div
                                key={day.format('YYYY-MM-DD')}
                                role={isDisabled ? undefined : 'button'}
                                onClick={isDisabled ? undefined : () => onSelectDay(day)}
                                className={[
                                    'booking-calendar-cell',
                                    !inMonth && 'booking-calendar-cell-muted',
                                    isDisabled && 'booking-calendar-cell-disabled',
                                    blocked && 'booking-calendar-cell-blocked',
                                    isSelected && !isDisabled && 'booking-calendar-cell-selected',
                                    isToday && !isSelected && 'booking-calendar-cell-today',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                            >
                                {day.date()}
                            </div>
                        );
                    })}
                </div>

                {blockedDates.length > 0 && (
                    <div className="booking-calendar-legend">
                        <span><i className="booking-calendar-legend-dot booking-calendar-legend-dot-blocked"></i>Unavailable</span>
                        <span><i className="booking-calendar-legend-dot booking-calendar-legend-dot-selected"></i>Selected</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingCalendar;
